import { format } from 'date-fns';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import styled from '@/styles';
import { ActivationBreakdown } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { InfoTipIcon20, XMarkIcon } from './icons';

// GRAVY-10429: the activation-mode "See breakdown" modal. Reliability workers
// keep ScoreHistoryModal; activation workers (fewer than 5 approved shifts) get
// this two-row view — Onboarding progress + Shifts-completed progress — which
// explains the activation score and the path to graduation. It deliberately
// reuses the same visual language as the reliability breakdown (ScoreHistoryModal):
// the stacked label|value header, the full-width "how healthy" bar, the count
// caption, and the info-icon footer.
const Rows = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});
const Row = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});
const RowHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});
const LabelGroup = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
});
const RowLabel = styled('div', {
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
});
// The grey "Locked" chip shown next to "Shifts completed" while onboarding is
// still incomplete — a muted neutral tint, distinct from the green/amber
// healthy/below-target pills of the reliability breakdown.
const LockedPill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  backgroundColor: dependabilityV2Colors.depMeterTrack,
  color: dependabilityV2Colors.depMetaText,
});
const MeterTrack = styled('div', {
  width: '100%',
  height: '12px',
  borderRadius: '100px',
  backgroundColor: dependabilityV2Colors.depMeterTrack,
  overflow: 'hidden',
});
const MeterFill = styled('div', {
  height: '12px',
  borderRadius: '100px',
});
const RowValue = styled('div', {
  fontSize: '20px',
  lineHeight: 1.4,
  fontWeight: 800,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  flex: 'none',
  variants: {
    status: {
      // green (onboarding complete) / amber (in progress) — same tokens the
      // reliability breakdown uses for ok/warn signal values.
      ok: { color: dependabilityV2Colors.depPositive },
      warn: { color: dependabilityV2Colors.depWarnText },
      // the locked "-" dash: neutral ink, not a status color.
      muted: { color: dependabilityV2Colors.depInk },
    },
  },
});
const Caption = styled('div', {
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
});
const Footer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '40px',
  paddingTop: '16px',
  borderTop: `1px solid ${dependabilityV2Colors.depBorder}`,
  fontSize: '$md',
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
});

// en dash shown as the locked shifts value (no percentage until unlocked).
const EN_DASH = '–';

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

// "Account created Aug 24, 2026" — same date-fns 'MMM d, yyyy' formatting the
// activity feed uses for a prior-year date. Guards a malformed ISO string.
const formatAccountDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'MMM d, yyyy');
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  breakdown: ActivationBreakdown;
}

const ActivationBreakdownModal = ({ isOpen, onClose, breakdown }: Props) => {
  const { onboarding, shifts, account_created_at } = breakdown;
  const onboardingStatus = onboarding.complete ? 'ok' : 'warn';
  const onboardingFill = onboarding.complete
    ? dependabilityV2Colors.depFeedPositive
    : dependabilityV2Colors.depFeedNeutral;
  // GRAVY-10429: onboarding caption.
  //  - complete            → "N of M steps completed"
  //  - rejected pending    → "N of M steps: {pending_step} rejected"
  //  - normal pending      → "N of M steps: {pending_step} pending"
  // Belt-and-suspenders: if the incomplete branch ever arrives with a missing
  // pending_step, fall back to the completed-style caption rather than render
  // the literal "null pending" an admin once saw (a backend bug let
  // complete=false with pending_step=null).
  const stepsPrefix = `${onboarding.steps_completed} of ${onboarding.steps_total} steps`;
  const onboardingCaption =
    onboarding.complete || !onboarding.pending_step
      ? `${stepsPrefix} completed`
      : `${stepsPrefix}: ${onboarding.pending_step} ${
          onboarding.pending_step_rejected ? 'rejected' : 'pending'
        }`;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      // Matches the reliability breakdown shell (ScoreHistoryModal): 650w,
      // radius 8, flat shadow, 70% scrim, 24px padding, 40px section gaps.
      PaperProps={{
        sx: {
          width: 650,
          maxWidth: '95vw',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } },
      }}
    >
      <DialogTitle
        sx={{
          padding: '24px 24px 0',
          fontSize: 18,
          fontWeight: 600,
          color: dependabilityV2Colors.depInk,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Activation score breakdown
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: dependabilityV2Colors.depInk }}
        >
          <XMarkIcon />
        </IconButton>
      </DialogTitle>
      {/* MUI forces paddingTop 0 on a DialogContent following a DialogTitle via
          a two-class sibling selector that outranks sx — override with !important. */}
      <DialogContent
        sx={{ padding: '40px 24px 24px', paddingTop: '40px !important' }}
      >
        <Rows data-testid="activation-breakdown">
          {/* Onboarding: green when complete, amber while in progress. */}
          <Row data-testid="onboarding-row">
            <RowHeader>
              <LabelGroup>
                <RowLabel>Onboarding</RowLabel>
              </LabelGroup>
              <RowValue
                status={onboardingStatus}
                data-testid="onboarding-value"
              >
                {onboarding.percent}%
              </RowValue>
            </RowHeader>
            <MeterTrack>
              <MeterFill
                style={{
                  width: `${clampPercent(onboarding.percent)}%`,
                  backgroundColor: onboardingFill,
                }}
              />
            </MeterTrack>
            <Caption data-testid="onboarding-caption">
              {onboardingCaption}
            </Caption>
          </Row>

          {/* Shifts completed: locked (empty grey bar + guidance) until
              onboarding is done, then an amber progress bar toward the
              5-shift graduation threshold. */}
          <Row data-testid="shifts-row">
            <RowHeader>
              <LabelGroup>
                <RowLabel>Shifts completed</RowLabel>
                {shifts.locked && (
                  <LockedPill data-testid="shifts-locked">Locked</LockedPill>
                )}
              </LabelGroup>
              {shifts.locked ? (
                <RowValue status="muted" data-testid="shifts-value">
                  {EN_DASH}
                </RowValue>
              ) : (
                <RowValue status="warn" data-testid="shifts-value">
                  {shifts.percent}%
                </RowValue>
              )}
            </RowHeader>
            <MeterTrack>
              {!shifts.locked && (
                <MeterFill
                  style={{
                    width: `${clampPercent(shifts.percent)}%`,
                    backgroundColor: dependabilityV2Colors.depFeedNeutral,
                  }}
                />
              )}
            </MeterTrack>
            <Caption data-testid="shifts-caption">
              {shifts.locked
                ? 'Finish onboarding to start claiming shifts'
                : `${shifts.remaining} more to reach full activation`}
            </Caption>
          </Row>
        </Rows>
        <Footer data-testid="activation-account-created">
          <InfoTipIcon20 />
          {`Account created ${formatAccountDate(account_created_at)}`}
        </Footer>
      </DialogContent>
    </Dialog>
  );
};

export default ActivationBreakdownModal;
